/**
 * @author  Agence Dn'D <contact@dnd.fr>
 * @license https://opensource.org/licenses/osl-3.0.php Open Software License (OSL 3.0)
 * @link    https://www.dnd.fr/
 */

class KitContents extends HTMLElement {
    connectedCallback() {
        this.wrapper = this.querySelector('[data-kit-list-wrapper]');
        this.list = this.querySelector('[data-kit-list]');
        this.toggle = this.querySelector('[data-kit-toggle]');
        this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
        this.threshold = parseInt(this.dataset.collapseThreshold, 10) || 5;

        // Without JS the list stays open, so the toggle is only rendered
        // when there is something to fold.
        if (!this.wrapper || !this.list || !this.toggle || this.items.length <= this.threshold) {
            return;
        }

        this.collapse();

        // The transition is enabled only once the initial fold is painted,
        // so the list doesn't animate on page load.
        requestAnimationFrame(() => this.setAttribute('data-ready', ''));

        this.toggle.addEventListener('click', () => this.onToggle());

        if ('ResizeObserver' in window) {
            this.observer = new ResizeObserver(() => {
                if (this.wrapper.hasAttribute('data-collapsed')) {
                    this.setCollapsedHeight();
                }
            });
            this.observer.observe(this.list);
        }
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    onToggle() {
        if (this.wrapper.hasAttribute('data-collapsed')) {
            this.expand();
            return;
        }

        this.collapse(true);

        // Bring the block title back on screen when the fold pushed it above
        // the viewport.
        if (this.getBoundingClientRect().top < 0) {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /**
     * Folded height: stops right at the end of the last fully shown row, so
     * the fade and the toggle both sit right after it instead of past a
     * partially cut-off row. Measured from the rows themselves, which keeps
     * it correct when a name wraps on mobile.
     */
    setCollapsedHeight() {
        const lastVisibleRow = this.items[this.threshold - 2];

        if (!lastVisibleRow) {
            return;
        }

        const listTop = this.list.getBoundingClientRect().top;
        const { top, height } = lastVisibleRow.getBoundingClientRect();

        this.wrapper.style.setProperty('--kit-collapsed-height', `${Math.round(top - listTop + height)}px`);
    }

    collapse(animated) {
        this.setCollapsedHeight();

        if (animated) {
            // Give the transition an explicit height to start from.
            this.wrapper.style.maxBlockSize = `${this.list.scrollHeight}px`;
            void this.wrapper.offsetHeight;
        }

        this.wrapper.setAttribute('data-collapsed', '');
        this.wrapper.style.maxBlockSize = '';
        this.setState(false);
    }

    expand() {
        // max-block-size can't animate towards "none", so the open state is
        // animated towards the measured height, then released once done.
        this.wrapper.style.maxBlockSize = `${this.list.scrollHeight}px`;
        this.wrapper.removeAttribute('data-collapsed');
        this.setState(true);

        // No transition runs under reduced motion, so transitionend never
        // fires: release the inline height right away instead of waiting.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.wrapper.style.maxBlockSize = '';
            return;
        }

        const onTransitionEnd = (event) => {
            if (event.target !== this.wrapper) {
                return;
            }

            this.wrapper.style.maxBlockSize = '';
            this.wrapper.removeEventListener('transitionend', onTransitionEnd);
        };

        this.wrapper.addEventListener('transitionend', onTransitionEnd);
    }

    setState(expanded) {
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.toggle.querySelector('[data-kit-toggle-more]').hidden = expanded;
        this.toggle.querySelector('[data-kit-toggle-less]').hidden = !expanded;

        this.items.forEach((item, index) => {
            const link = item.querySelector('.kit-contents__link');

            if (!link) {
                return;
            }

            if (expanded || index < this.threshold - 1) {
                link.removeAttribute('tabindex');
                link.removeAttribute('inert');
                return;
            }

            link.setAttribute('tabindex', '-1');
            link.setAttribute('inert', '');
        });
    }
}

customElements.define('kit-contents', KitContents);
